import { ComponentFields, Field, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';

import { LearningTypes } from 'src/enums/LearningTypes';

import { ContentDetailItem } from 'src/types/ContentDetailsItem';
import { ExtendedItem } from 'src/types/ExtendedItem';
import { ResourceListItem } from 'src/types/ResourceListItem';

import { parseAccessLevelRouteFields } from '../fields/parseAccessLevelContent';
import { parseAccessTypes, parseAccessTypesString } from '../fields/parseAccessTypes';
import { parseIsoDate } from '../fields/parseDate';
import { parseLearningFormats } from '../fields/parseLearningFormats';
import { parseTags } from '../fields/parseTags';
import { parseTypesOfResources, parseTypesOfResourcesString } from '../fields/parseTypesOfResources';
import { parseWildCardUrl } from '../fields/parseUrl';

export function mapLearningModule(item: ExtendedItem, wildcardUrl?: string): ResourceListItem | null {
	try {
		if (!item) {
			return null;
		}

		return {
			Id: item.id,
			Title: (item.fields.Title as Field<string>).value,
			Strapline: (item.fields.Strapline as Field<string>)?.value,
			ImageUrl: item.fields.Image ? (item.fields.Image as ImageField).value?.src : '',
			LearningFormats: parseLearningFormats(item.fields.LearningFormat),
			TypesOfResources: parseTypesOfResources(item.fields.Tags),
			TypesOfResourcesString: parseTypesOfResourcesString(item.fields.Tags),
			Url: parseWildCardUrl(item.name, wildcardUrl),
			LearningType: LearningTypes.Course.toString(),
			AccessTypes: parseAccessTypes(item.fields.AccessType),
			AccessTypesString: parseAccessTypesString(item.fields.AccessType),
			Tags: parseTags(item.fields.Tags),
			CpdHours: (item.fields.CPDHours as Field<string>)?.value,
		};
	} catch (error) {
		console.error(error);
		return null;
	}
}

export function mapLearningModuleToContentDetails(
	id: string,
	fields: ComponentFields | undefined,
	RCNiSAMLIdPBaseUrl: string,
	RCNiImpelsysSSORelayStateParameterName: string,
	RCNiImpelsysSSOSpentityId: string,
	RCNiImpelsysSSOSpentityIdParameterName: string
): ContentDetailItem | null {
	try {
		if (!fields) {
			return null;
		}

		const dateUpdated = fields.DateUpdated ? parseIsoDate((fields.DateUpdated as Field<string>).value) : null;
		const publishedDate = fields['Published Date'] ? parseIsoDate((fields['Published Date'] as Field<string>).value) : null;

		let url = '';
		try {
			url = `${RCNiSAMLIdPBaseUrl}
							?${RCNiImpelsysSSOSpentityIdParameterName}=${RCNiImpelsysSSOSpentityId}
							&${RCNiImpelsysSSORelayStateParameterName}=${(fields.RCNiArticleUrl as Field<string>).value}`;
		} catch (error) {
			console.error('Failed to calculate RCNi link' + error);
		}

		return {
			Id: id,
			Title: (fields.Title as Field<string>).value,
			Strapline: (fields.Strapline as Field<string>)?.value,
			ImageUrl: fields.Image ? (fields.Image as ImageField).value?.src : '',
			Tags: parseTags(fields.Tags),
			AccessTypes: parseAccessTypes(fields.AccessType),
			AccessTypesString: parseAccessTypesString(fields.AccessType),
			Access: parseAccessLevelRouteFields(fields),
			TypesOfResources: parseTypesOfResources(fields.Tags),
			TypesOfResourcesString: parseTypesOfResourcesString(fields.Tags),
			DateUpdated: dateUpdated,
			Information: (fields.Information as Field<string>).value,
			IsCpdArticle: false,
			IsLmsModule: true,
			IsLmsCourse: false,
			IsResource: false,
			DatePublished: publishedDate,
			LearningFormats: parseLearningFormats(fields.LearningFormat),
			CPDHours: (fields.CPDHours as Field<string>).value,
			CertificateOfCompletion: fields.CertificateOfCompletion && (fields.CertificateOfCompletion as Field<boolean>).value === true,
			ProfessionalLeads: { Text: (fields.ProfessionalLeads as Field<string>).value },
			ResourceSponsor: (fields.ResourceSponsor as Field<string>).value,
			PeerReviewed: fields.PeerReviewed && (fields.PeerReviewed as Field<boolean>).value === true,
			LearningTypeString: LearningTypes.Course.toString(),
			LearningType: LearningTypes.Course,
			Url: url,
			Cost: (fields.Cost as Field<string>).value,
			Location: (fields.Location as Field<string>)?.value,
		};
	} catch (error) {
		console.error(error);
		return null;
	}
}
